'use client';

import React from 'react';
import Link from 'next/link';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { ClickableBuildingCard } from '@/components/cards/ClickableBuildingCard';
import { Carousel } from '@/components/carousel';
import { cn } from '@/lib/cn';
import { useOwner } from '../../context/OwnerProvider';
import { analyticsDataAttrs } from '@/lib/analytics/data-attrs';
import { contactUrl } from '@/utils/routes';

export function OwnerAssociatedBuildings() {
  const { buildings, owner, loading } = useOwner();

  const ownerName = owner.name || 'this owner';
  const ownerUrl = contactUrl(owner);

  // Show section even if no buildings (will show empty state or loading)
  const displayBuildings = buildings?.slice(0, 4) || [];

  return (
    <div className="space-y-2">
      {/* Section Header */}
      <Link href={`${ownerUrl}/buildings`} rel="nofollow">
        <SectionHeader title={`Buildings associated with ${ownerName}`} trailingIcon="default" />
      </Link>

      <div className={cn('px-2', 'md:px-4')}>
        {loading ? (
          <div className={cn('grid grid-cols-2 gap-4')}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className={cn('animate-pulse bg-secondary rounded-lg h-64')} />
            ))}
          </div>
        ) : displayBuildings.length === 0 ? (
          <div className={cn('py-8 text-center')}>
            <p className={cn('text-body-2 text-secondary')}>No buildings found</p>
          </div>
        ) : (
          <>
            {/* Mobile Carousel */}
            <Carousel
              className="col-span-full md:hidden -mx-6"
              carouselId="owner-buildings"
              items={displayBuildings.map(building => ({
                id: building.id,
                content: (
                  <ClickableBuildingCard
                    key={building.id}
                    building={building}
                    {...(building.images?.[0]?.url && { imageUrl: building.images[0].url })}
                    {...analyticsDataAttrs('contact_buildingcard', {
                      buildingId: building.slug,
                      addressSlug: building.primaryAddress?.slug ?? '',
                    })}
                  />
                ),
              }))}
              padStart={{ mobile: 24, tablet: 12, desktop: 12 }}
              padEnd={{ mobile: 24, tablet: 12, desktop: 12 }}
              perView={{ mobile: 1, tablet: 2, desktop: 3 }}
              peek={{ mobile: 80, tablet: 0, desktop: 0 }}
              gap={16}
              ariaLabel="Buildings associated with owner"
            />

            {/* Desktop Grid */}
            <div className={cn('gap-4', 'grid-cols-2', 'hidden md:grid')}>
              {displayBuildings.map(building => (
                <ClickableBuildingCard
                  key={building.id}
                  building={building}
                  {...(building.images?.[0]?.url && { imageUrl: building.images[0].url })}
                  {...analyticsDataAttrs('contact_buildingcard', {
                    buildingId: building.slug,
                    addressSlug: building.primaryAddress?.slug ?? '',
                  })}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
